import React from "react";
import {
    IAvailabilitySchedule,
    IClinician,
    RecurrenceRule,
    ServiceDeliveryOption,
} from "intake_shared/src/types";
import { Dialog } from "../../../bits/Dialog/Dialog";
import { Select } from "../../../bits/Select/Select";
import { Input } from "../../../bits/Input/Input";
import CustomDateSelector from "../../../bits/CustomDateSelector/CustomDateSelector";
import Alert from "../../../bits/Alert/Alert";
import { CacheContext } from "../../../../../contexts/CacheContext";
import { getAvailabilityRecurrenceOptions } from "../../../../v2utils";

interface ReserveTimeWindowDialogProps {
    open: boolean;
    onClose: () => void;
    loggedInClinician: IClinician;
    availabilitySchedules: IAvailabilitySchedule[];
    heldBookingSchedules: IAvailabilitySchedule[];
    confirmedBookingSchedules: IAvailabilitySchedule[];
    officeWindowSchedules: IAvailabilitySchedule[];
    clinicianServiceSelection: { [key: string]: { services: string[] } };
    requestedServices: { [key: string]: string };
    onTimeWindowReserved: () => void;
    draftReservedWindow: any;
    setDraftReservedWindow: (data: any) => void;
    onSave: () => void;
}

export default function ReserveTimeWindowDialog({
    open,
    onClose,
    loggedInClinician,
    availabilitySchedules,
    heldBookingSchedules,
    confirmedBookingSchedules,
    officeWindowSchedules,
    clinicianServiceSelection,
    requestedServices,
    onTimeWindowReserved,
    draftReservedWindow,
    setDraftReservedWindow,
    onSave,
}: ReserveTimeWindowDialogProps) {
    const allowAnyServiceOneOff =
        loggedInClinician.departments?.includes("CHP") ?? false;
    const serviceOptions = [
        { value: "", label: "Any service" },
        ...(clinicianServiceSelection[loggedInClinician.id!]?.services.map(
            (serviceKey) => ({
                value: serviceKey,
                label: requestedServices[serviceKey],
            })
        ) || []),
    ];
    const olc = React.useContext(CacheContext);
    const allDepartmentsAndServices = olc?.allDepartmentAndServices ?? {};
    const [selectedPractice, setSelectedPractice] = React.useState(
        draftReservedWindow.practice_id || ""
    );

    const recurrenceOptions = getAvailabilityRecurrenceOptions(
        draftReservedWindow.availableService,
        requestedServices,
        allowAnyServiceOneOff
    );

    const serviceDeliveryOptions = [
        { value: "in_person" as ServiceDeliveryOption, label: "In-Person" },
        { value: "telemed" as ServiceDeliveryOption, label: "Telemed" },
    ];

    const practiceOptions =
        loggedInClinician?.departments?.map((department) => ({
            value: department,
            label: department?.replaceAll("_", " "),
        })) || [];

    return (
        <Dialog
            open={open}
            onClose={onClose}
            title="Reserve time window"
            onSave={onSave}
            onCancel={onClose}
            saveText="Reserve Window"
            cancelText="Cancel"
        >
            <div className="v2-form">
                <Alert type="info">
                    Use this to reserve time windows for specific client. You need to
                    know the client's email before you can reserve a time slot for
                    them. When you reserve a time window, the intake team will see
                    this when booking the client. Reserved time windows will appear
                    on your availability screen as "Reserved".
                </Alert>

                <Input
                    label="Client's email"
                    type="email"
                    placeholder="Client's email"
                    value={draftReservedWindow.reservedFor}
                    onChange={(e) => {
                        setDraftReservedWindow({
                            ...draftReservedWindow,
                            reservedFor: e.target.value,
                        });
                    }}
                />

                <Select
                    label="Service"
                    value={serviceOptions.find(
                        (option) =>
                            option.value === draftReservedWindow.availableService
                    )}
                    onChange={(option) => {
                        if (
                            option &&
                            typeof option === "object" &&
                            "value" in option
                        ) {
                            const availableService = option.value as string;
                            const availableRecurrenceOptions =
                                getAvailabilityRecurrenceOptions(
                                    availableService,
                                    requestedServices,
                                    allowAnyServiceOneOff
                                );
                            setDraftReservedWindow({
                                ...draftReservedWindow,
                                availableService,
                                recurrenceRule: availableRecurrenceOptions.some(
                                    (recurrenceOption) =>
                                        recurrenceOption.value ===
                                        draftReservedWindow.recurrenceRule
                                )
                                    ? draftReservedWindow.recurrenceRule
                                    : RecurrenceRule.WEEKLY,
                            });
                        }
                    }}
                    options={serviceOptions}
                    isLongListInDialog
                />
                <Select
                    label="Recurring"
                    value={recurrenceOptions.find(
                        (option) =>
                            option.value === draftReservedWindow.recurrenceRule
                    )}
                    onChange={(option) => {
                        if (
                            option &&
                            typeof option === "object" &&
                            "value" in option
                        ) {
                            setDraftReservedWindow({
                                ...draftReservedWindow,
                                recurrenceRule: option.value as RecurrenceRule,
                            });
                        }
                    }}
                    options={recurrenceOptions}
                    isLongListInDialog
                />

                <CustomDateSelector
                    onChange={(from, to) => {
                        setDraftReservedWindow({
                            ...draftReservedWindow,
                            from,
                            to,
                        });
                    }}
                />

                {loggedInClinician?.departments?.length > 1 && (
                    <Select
                        label="Clinician Practice"
                        options={practiceOptions}
                        value={
                            practiceOptions.find(
                                (option) => option.value === selectedPractice
                            ) || { label: "", value: "" }
                        }
                        isRequired
                        onChange={(val) => {
                            setSelectedPractice((val as any)?.value);
                            setDraftReservedWindow({
                                ...draftReservedWindow,
                                practice_id: allDepartmentsAndServices[
                                    (val as any)?.value
                                ]?.practice_id as string,
                            });
                        }}
                        isLongListInDialog
                    />
                )}

                <Select
                    label="In-Person or Telemed"
                    value={serviceDeliveryOptions.find(
                        (option) =>
                            option.value ===
                            draftReservedWindow.serviceDeliveryOption
                    )}
                    onChange={(option) => {
                        if (
                            option &&
                            typeof option === "object" &&
                            "value" in option
                        ) {
                            setDraftReservedWindow({
                                ...draftReservedWindow,
                                serviceDeliveryOption:
                                    option.value as ServiceDeliveryOption,
                            });
                        }
                    }}
                    options={serviceDeliveryOptions}
                    isLongListInDialog
                />
            </div>
        </Dialog>
    );
}
